/**
 * Nowa Aura — cart drawer cross-sell custom element.
 *
 * Fetches gender-matched complementary product recommendations for the most
 * recently added cart line (Shopify's /recommendations/products endpoint,
 * falling back from intent=complementary to intent=related), excludes
 * products already in the cart, keeps up to 3, and renders them with an
 * inline size-select + add-to-cart button.
 *
 * Loaded as an external, unconditionally-parsed asset (not an inline
 * <script>) because Horizon's drawer morph mechanism replaces the mount
 * point's markup on every cart update, and an inline <script> inside
 * replaced content does not re-execute after the first render.
 *
 * This theme's cart components listen for the "shopify:cart:lines-update"
 * document event (confirmed by reading assets/cart-icon.js and
 * assets/component-cart-items.js on this theme) rather than an older
 * "cart:update"/"cart:add" name, and read `event.promise` directly off the
 * event instance (not `event.detail.promise`). Both the add-to-cart handler
 * here and the refresh listener match that exact contract.
 */
(function () {
  if (customElements.get('na-cart-cross-sell')) return;

  var MAX_ITEMS = 1;
  var RECOMMENDATION_LIMIT = 10;

  function computeGender(productType) {
    var pt = (productType || '').toLowerCase().trim();
    return pt.indexOf(' men') !== -1 ? 'meskie' : 'damskie';
  }

  class NaCartCrossSellElement extends HTMLElement {
    connectedCallback() {
      this._requestToken = 0;
      this._onCartUpdate = this._onCartUpdate.bind(this);
      document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
    }

    _onCartUpdate() {
      // Any cart change (from this element's own add-to-cart, the main PDP
      // button, or elsewhere) can change which item was most recently added,
      // so always re-derive the recommendation from a fresh /cart.js read.
      this.refresh();
    }

    async refresh() {
      // Re-query on every call rather than caching in connectedCallback:
      // Horizon's drawer morph can upgrade this custom element (firing
      // connectedCallback) before its children are attached, which would
      // otherwise permanently cache a null reference.
      var list = this.querySelector('[data-role="nau-list"]');
      if (!list) return;

      // Concurrent refresh() calls can overlap (e.g. this element's own
      // add-to-cart dispatch and the drawer morph both fire
      // "shopify:cart:lines-update" around the same time). Only the most
      // recently started call is allowed to write to the DOM, so a slower
      // earlier call can't clobber a newer result.
      var token = ++this._requestToken;
      try {
        var cart = await (await fetch('/cart.js')).json();
        if (token !== this._requestToken) return;

        if (!cart.items || cart.items.length === 0) {
          list.innerHTML = '';
          this.hidden = true;
          return;
        }

        var lastItem = cart.items[cart.items.length - 1];
        var excludeHandles = cart.items.map(function (i) {
          return i.handle;
        });
        var gender = computeGender(lastItem.product_type);
        var productId = lastItem.product_id;

        var cards = await this._fetchCandidates(productId, 'complementary');
        var matches = this._filterCards(cards, gender, excludeHandles);

        if (matches.length < 1) {
          cards = await this._fetchCandidates(productId, 'related');
          matches = this._filterCards(cards, gender, excludeHandles);
        }

        if (token !== this._requestToken) return;
        this._render(list, matches.slice(0, MAX_ITEMS));
      } catch (e) {
        console.warn('[na-cart-cross-sell] refresh failed', e);
      }
    }

    async _fetchCandidates(productId, intent) {
      var url =
        '/recommendations/products?section_id=na_cart_upsell&product_id=' +
        encodeURIComponent(productId) +
        '&intent=' +
        intent +
        '&limit=' +
        RECOMMENDATION_LIMIT;
      var res = await fetch(url);
      if (!res.ok) return [];
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.prototype.slice.call(doc.querySelectorAll('.nau-card'));
    }

    _filterCards(cards, gender, excludeHandles) {
      return cards.filter(function (card) {
        if (card.dataset.available === 'false') return false;
        if (card.dataset.gender !== gender) return false;
        if (excludeHandles.indexOf(card.dataset.handle) !== -1) return false;
        return true;
      });
    }

    _render(list, cards) {
      list.innerHTML = '';
      if (cards.length === 0) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      var self = this;
      cards.forEach(function (card) {
        var clone = card.cloneNode(true);
        self._wireCard(clone);
        list.appendChild(clone);
      });
    }

    _wireCard(card) {
      var sizeField = card.querySelector('[data-role="nau-size"]');
      var addBtn = card.querySelector('[data-role="nau-add"]');
      if (!addBtn) return;

      if (sizeField && sizeField.tagName === 'SELECT') {
        sizeField.addEventListener('change', function () {
          if (sizeField.value) {
            addBtn.dataset.variantId = sizeField.value;
            addBtn.disabled = false;
          } else {
            addBtn.disabled = true;
          }
        });
      }

      var self = this;
      addBtn.addEventListener('click', function () {
        if (addBtn.disabled) return;
        self._addToCart(addBtn);
      });
    }

    async _addToCart(button) {
      var variantId = button.dataset.variantId;
      if (!variantId) return;
      button.disabled = true;
      var originalText = button.textContent;
      button.textContent = '…';

      var resultPromise = this._performAdd(variantId);

      // Match this theme's real event contract: cart-icon.js and
      // component-cart-items.js both read `event.promise` directly off the
      // dispatched event (not event.detail.promise), and resolve it to
      // { cart: { totalQuantity }, detail: { sections } }.
      var evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      evt.action = 'add';
      evt.context = 'cart';
      evt.lines = [{ merchandiseId: variantId, quantity: 1 }];
      evt.promise = resultPromise;
      document.dispatchEvent(evt);

      try {
        await resultPromise;
        button.textContent = '✓';
        button.classList.add('nau-added');
      } catch (e) {
        console.warn('[na-cart-cross-sell] add to cart failed', e);
        button.textContent = originalText;
        button.disabled = false;
      }
    }

    async _performAdd(variantId) {
      var addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      });
      if (!addRes.ok) {
        var err = await addRes.json().catch(function () {
          return {};
        });
        throw new Error(err.description || 'add to cart failed');
      }
      var addData = await addRes.json();

      // A theme can only write cart attributes, which reach the order as
      // note_attributes (queryable later as order.customAttributes) — line
      // item properties don't surface there. This stamps the cart so this
      // drawer's own contribution is measurable, distinct from the PDP
      // complete-the-look block's own `_na_cross_sell_pdp` key
      // (blocks/na_look_complete.liquid).
      var cart = await (
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: { _na_cross_sell_cart: 'cart-drawer' },
          }),
        })
      ).json();

      // Refresh our own list for the new most-recently-added item once the
      // drawer content has settled.
      this.refresh();

      return {
        cart: { totalQuantity: cart.item_count },
        detail: { sections: addData.sections || {} },
      };
    }
  }

  customElements.define('na-cart-cross-sell', NaCartCrossSellElement);
})();
